import React, { useState } from 'react'
import Select from 'react-select'
import { Input, Button } from 'reactstrap/lib'
import 'react-select/dist/react-select.css'
import { isAmazon } from 'suredone-common'
import Highlighter from 'react-highlight-words'
import { useQuery } from '@apollo/react-hooks'
import gql from 'graphql-tag'
import { FaChevronRight as RightArrow, FaChevronDown as DownArrow } from 'react-icons/fa'

const GET_AMAZON_PRODUCT_TYPES_FOR_FIELD = gql`
  query GetAmazonProductTypesForField($slug: String!, $instanceNumber: Int!, $_id: String!) {
    ChannelInstance(slug: $slug, instanceNumber: $instanceNumber) {
      id
      amazonAttributes (_id: $_id) {
        _id
        product_types
      }
    }
  }
`

const customStyles = {
  fontWeight: 600,
  letterSpacing: '.3px'
}

const ExpandedFieldList = ({
  field,
  searchTerms,
  showInput,
  options,
  parentCallback,
  productTypes = [],
  productTypesLoading = false,
  productTypesError = null,
  listItems,
  setListItems
}) => {
  const [newItemProductType, setNewItemProductType] = useState(null)
  const [newItemOption, setNewItemOption] = useState(null)
  const [newItemInputValue, setNewItemInputValue] = useState({ value: '' })
  const { label, description } = field

  const addNewItem = () => {
    // Only add if product type is selected and either option or input value is provided
    if (!newItemProductType || (!newItemOption && !newItemInputValue?.value)) {
      return
    }

    const productTypeValue = newItemProductType?.value || newItemProductType?.label || newItemProductType
    const parentName = field?.name || field?.label

    const newItem = {
      id: `${parentName} - ${productTypeValue}`,
      productType: newItemProductType,
      selectedOption: showInput ? null : newItemOption,
      inputValue: showInput ? newItemInputValue : { value: '' }
    }
    setListItems([...listItems, newItem])

    // Reset the form after adding
    setNewItemProductType(null)
    setNewItemOption(null)
    setNewItemInputValue({ value: '' })
  }

  const removeItem = (id) => {
    setListItems(listItems.filter(item => item.id !== id))
  }

  const handleItemChange = (value, itemId, field) => {
    // Get the current item before updating
    const currentItem = listItems.find(item => item.id === itemId)

    setListItems(listItems.map(item =>
      item.id === itemId
        ? { ...item, [showInput ? 'inputValue' : 'selectedOption']: value }
        : item
    ))

    // Create a separate field identity for the child item
    if (currentItem) {
      const productTypeValue = currentItem.productType?.value || currentItem.productType?.label || currentItem.productType
      const childFieldName = `${field.label} - ${productTypeValue}`

      const childField = {
        ...field,
        name: childFieldName,
        label: childFieldName,
        parentField: field.name
      }

      parentCallback(value, childField)
    }
  }

  const usedProductTypes = listItems.map(item => {
    const productTypeValue = item.productType?.value || item.productType?.label || item.productType
    return productTypeValue
  }).filter(Boolean) // Remove any null/undefined values

  const availableProductTypes = (productTypes || []).filter(type => !usedProductTypes.includes(type))

  const productTypeOptions = availableProductTypes.map(type => ({
    value: type,
    label: <strong style={customStyles}>{type}</strong>
  }))

  const canAddItem = newItemProductType && (showInput ? newItemInputValue?.value : newItemOption)

  return (
    <div className='mt-2 border-top pt-2'>
      {listItems.length > 0 && (
        <div>
          {listItems.map((item) => (
            <div key={item.id} className='d-flex w-100 justify-content-between align-items-center flex-wrap mb-2 p-2 border rounded bg-light'>
              <div className='col-12 col-sm-7 col-lg-8 px-0 pr-sm-3 pb-2 pb-sm-0'>
                <strong style={customStyles}>
                  <Highlighter
                    className=''
                    highlightTag='mark'
                    autoEscape
                    searchWords={searchTerms || []}
                    textToHighlight={label || 'Unnamed field'}
                  /><br />
                </strong>
                {description}
                <div className='mt-1'>
                  <small className='text-muted'>
                    Product Type: <strong>{item.productType?.label || item.productType?.value}</strong>
                  </small>
                </div>
              </div>
              <div className='col-12 col-sm-5 col-lg-4 px-0 d-flex'>
                <div className='flex-grow-1 mr-2'>
                  {showInput
                    ? (
                      <Input
                        value={item.inputValue?.value || ''}
                        onChange={(e) => handleItemChange({ value: e.target.value }, item.id, field)}
                      />
                      )
                    : (
                      <Select
                        placeholder=''
                        options={options}
                        value={item.selectedOption}
                        onChange={(value) => handleItemChange(value, item.id, field)}
                      />
                      )}
                </div>
                <Button color='danger' size='sm' onClick={() => removeItem(item.id)}>
                  ×
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Add new item section - always visible */}
      <div className='mb-3 p-3 border rounded bg-light'>
        <div className='row'>
          <div className='col-12 col-md-5 mb-2'>
            <label className='form-label small text-muted'>Product Type</label>
            {productTypesError
              ? (
                <div className='text-danger small'>Error loading product types</div>
                )
              : null}
            <Select
              placeholder='Select product type...'
              options={productTypeOptions}
              value={newItemProductType}
              onChange={setNewItemProductType}
              isLoading={productTypesLoading}
              isDisabled={productTypesLoading}
              isSearchable
              isClearable
              noOptionsMessage={() => 'No product types found'}
              filterOption={(option, inputValue) => {
                return option.value.toLowerCase().includes(inputValue.toLowerCase())
              }}
            />
          </div>
          <div className='col-12 col-md-5 mb-2'>
            <label className='form-label small text-muted'>{showInput ? 'Field Value' : 'Field Option'}</label>
            {showInput
              ? (
                <Input
                  placeholder='Enter field value...'
                  value={newItemInputValue?.value || ''}
                  onChange={(e) => setNewItemInputValue({ value: e.target.value })}
                />
                )
              : (
                <Select
                  placeholder='Select field option...'
                  options={options}
                  value={newItemOption}
                  onChange={setNewItemOption}
                />
                )}
          </div>
          <div className='col-12 col-md-2 d-flex align-items-end mb-2'>
            <Button
              color='primary'
              size='sm'
              onClick={addNewItem}
              disabled={!canAddItem || productTypesLoading}
              className='w-100'
            >
              Add
            </Button>
          </div>
        </div>
        {!canAddItem && !productTypesLoading && (
          <small className='text-muted'>Please select product type and {showInput ? 'enter a field value' : 'select a field option'} to add a product type override.</small>
        )}
      </div>
    </div>
  )
}

const AmazonFieldSummary = ({
  slug,
  field,
  searchTerms,
  parentCallback,
  orgMappableFields,
  instanceNumber,

  isExpanded = false,
  onExpandedChange,
  listItems = [],
  onListItemsChange
}) => {
  const { label, description, value } = field
  const { channelFieldName } = value || {}

  const { loading: productTypesLoading, error: productTypesError, data: productTypesData } = useQuery(
    GET_AMAZON_PRODUCT_TYPES_FOR_FIELD,
    {
      variables: {
        slug,
        instanceNumber,
        _id: field.name
      },
      skip: !isExpanded,
      errorPolicy: 'all'
    }
  )

  const productTypesFromQuery = productTypesData?.ChannelInstance?.amazonAttributes?.[0]?.product_types || []

  const productTypes = productTypesFromQuery

  const isAmazonChannel = isAmazon(slug)
  const showInput = isAmazonChannel && field?.name?.includes('/0/unit')
  const [inputValue, setInputValue] = useState({ value: (channelFieldName || null) })
  const [selectedOption, setSelectedOption] = useState({
    value: (channelFieldName || null),
    label: <strong style={customStyles}>{channelFieldName || null}</strong>
  })

  const options = (orgMappableFields || []).map(item => ({
    value: item.name,
    label: <><strong style={customStyles}>{item.name}</strong><br />{item.description}</>
  }))

  const handleChangeSelect = (event) => {
    setSelectedOption(event)
    parentCallback(event, field)

    // If parent value is cleared, reset children and collapse
    if (!event?.value) {
      onListItemsChange && onListItemsChange([])
      onExpandedChange && onExpandedChange(false)
    }
  }

  const handleChangeInput = e => {
    const value = e.target.value
    setInputValue({ value })
    parentCallback({ value }, field)

    // If parent value is cleared, reset children and collapse
    if (!value || value.trim() === '') {
      onListItemsChange && onListItemsChange([])
      onExpandedChange && onExpandedChange(false)
    }
  }

  const hasParentValue = showInput
    ? inputValue?.value && inputValue.value.trim() !== ''
    : selectedOption?.value && selectedOption.value !== null

  const isExpandable = hasParentValue

  return (
    <div className='d-flex flex-column w-100'>
      <div
        className='d-flex w-100 justify-content-between align-items-center flex-wrap'
        onClick={isExpandable
          ? () => {
              onExpandedChange && onExpandedChange(!isExpanded)
            }
          : undefined}
      >
        <div className='col-12 col-sm-7 col-lg-8 px-0 pr-sm-3 pb-2 pb-sm-0'>
          <div className='d-flex align-items-center'>
            {isExpandable && (
              isExpanded
                ? (
                  <DownArrow className='mr-2 text-muted' style={{ fontSize: '12px' }} />
                  )
                : (
                  <RightArrow className='mr-2 text-muted' style={{ fontSize: '12px' }} />
                  )
            )}
            <div style={isExpandable ? {} : { marginLeft: '20px' }}>
              <strong style={customStyles}>
                <Highlighter
                  className=''
                  highlightTag='mark'
                  autoEscape
                  searchWords={searchTerms || []}
                  textToHighlight={label || 'Unnamed field'}
                /><br />
              </strong>
              {description}
              {listItems.length > 0 && (
                <div className='mt-1'>
                  <small className='text-muted'>
                    ({listItems.length} Product type{listItems.length !== 1 ? 's' : ''} overridden)
                  </small>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className='col-12 col-sm-5 col-lg-4 px-0' onClick={(e) => e.stopPropagation()}>
          {showInput
            ? (
              <Input
                value={inputValue?.value}
                onChange={handleChangeInput}
              />
              )
            : (
              <Select
                placeholder=''
                options={options}
                value={selectedOption}
                onChange={handleChangeSelect}
              />
              )}
        </div>
      </div>
      {isExpanded && (
        <ExpandedFieldList
          field={field}
          searchTerms={searchTerms}
          showInput={showInput}
          options={options}
          parentCallback={parentCallback}
          productTypes={productTypes}
          productTypesLoading={productTypesLoading}
          productTypesError={productTypesError}
          listItems={listItems}
          setListItems={onListItemsChange}
        />
      )}
    </div>
  )
}

export default AmazonFieldSummary
